"use client"

import { useState } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import { Button } from "@/components/ui/button"
import { ProductInfoButton } from "@/components/ui/product-info-modal"
import { MRI_PACKAGE_INFO, ADDON_INFO } from "@/data/product-info"
import type { ProductInfoData } from "@/components/ui/product-info-modal"
import { formatCurrency } from "@/lib/format"
import type { PriceMap } from "@/lib/pricing"
import type { Answers } from "@/lib/quiz/types"

interface Props {
  answers: Answers
  prices: PriceMap
  onSubmit: (patch: Partial<Answers>) => void
  isFocus?: boolean
}

interface Followup {
  key: "brain" | "calcium" | "dexa" | "mamma"
  info: ProductInfoData
  /** Pricing-map key for the +CHF suffix. null = no separate fee (brain
   *  is a tier upgrade priced at the Pro vs Advanced delta and not an
   *  add-on). */
  priceKey: string | null
}

/**
 * Dedicated step for the imaging add-ons. Reachable only when the
 * user picked Yes on the MRI step — `stepsFor()` inserts this step
 * dynamically when `answers.mri_interest === true`. The MRI itself
 * is already committed; this screen is about whether to add detailed
 * brain analysis, heart calcium score, DEXA, mamma MRI.
 */
export function MriAddonsStep({
  answers,
  prices,
  onSubmit,
  isFocus = false,
}: Props) {
  const t = useTranslations("quiz")
  const { code: country } = useCountry()

  const [brain, setBrain] = useState(answers.brain_interest === true)
  const [calcium, setCalcium] = useState(answers.calcium_interest === true)
  const [dexa, setDexa] = useState(answers.dexa_interest === true)
  const [mamma, setMamma] = useState(answers.mamma_interest === true)

  const mammaEligible =
    answers.gender === "female" &&
    typeof answers.age === "number" &&
    answers.age >= 30

  const handleSubmit = () => {
    onSubmit({
      brain_interest: brain,
      calcium_interest: calcium,
      dexa_interest: dexa,
      mamma_interest: mammaEligible ? mamma : false,
    })
  }

  const handleSkip = () => {
    onSubmit({
      brain_interest: false,
      calcium_interest: false,
      dexa_interest: false,
      mamma_interest: false,
    })
  }

  const followups: Followup[] = [
    { key: "brain", info: MRI_PACKAGE_INFO.Pro, priceKey: null },
    { key: "calcium", info: ADDON_INFO.addonHeartCT, priceKey: "addonHeartCT" },
    { key: "dexa", info: ADDON_INFO.addonDEXA, priceKey: "addonDEXA" },
  ]
  if (mammaEligible) {
    followups.push({
      key: "mamma",
      info: ADDON_INFO.addonMammaMRI,
      priceKey: "addonMammaMRI",
    })
  }

  const stateFor = (k: Followup["key"]) =>
    k === "brain"
      ? brain
      : k === "calcium"
        ? calcium
        : k === "dexa"
          ? dexa
          : mamma
  const setFor = (k: Followup["key"], v: boolean) => {
    if (k === "brain") setBrain(v)
    else if (k === "calcium") setCalcium(v)
    else if (k === "dexa") setDexa(v)
    else setMamma(v)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        {followups.map((f) => {
          const checked = stateFor(f.key)
          const rawPrice = f.priceKey ? prices[f.priceKey] : null
          const priceLabel = rawPrice
            ? ` (+${formatCurrency(Number(rawPrice), country)})`
            : ""
          return (
            <label
              key={f.key}
              className={
                "flex cursor-pointer items-start gap-3 rounded-[12px] border-[1.6px] bg-white p-4 transition-all duration-150 ease-[cubic-bezier(0.25,1,0.5,1)] active:scale-[0.99] " +
                (checked
                  ? "border-aubergine-800"
                  : "border-neutral-200 hover:border-neutral-400")
              }
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setFor(f.key, e.target.checked)}
                className="sr-only"
              />
              <span
                className={
                  "mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 text-white transition-colors " +
                  (checked
                    ? "border-aubergine-800 bg-aubergine-800"
                    : "border-neutral-300 bg-white")
                }
                aria-hidden="true"
              >
                {checked && (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5.5 12L9.94444 16L18.5 8" />
                  </svg>
                )}
              </span>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-1">
                  <span className="type-body-lg text-neutral-900">
                    {t(`steps.mri.followups.${f.key}.label`)}
                  </span>
                  {priceLabel && (
                    <span className="type-body-sm text-neutral-500">
                      {priceLabel}
                    </span>
                  )}
                  <span onClick={(e) => e.preventDefault()}>
                    <ProductInfoButton data={f.info} biomarkerGroups={[]} />
                  </span>
                </div>
                <p className="type-body-sm mt-1 text-neutral-500">
                  {t(`steps.mri.followups.${f.key}.description`)}
                </p>
              </div>
            </label>
          )
        })}
      </div>

      {isFocus ? (
        <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:justify-center sm:gap-4">
          <Button
            variant="outline"
            onClick={handleSkip}
            size="lg"
            className="min-w-[200px]"
          >
            {t("skip")}
          </Button>
          <Button onClick={handleSubmit} size="lg" className="min-w-[200px]">
            {t("next")}
          </Button>
        </div>
      ) : (
        <Button onClick={handleSubmit}>{t("next")}</Button>
      )}
    </div>
  )
}
